import { db, sendPasswordResetEmail } from '../firebase';
import { collection, addDoc, doc, getDoc, getDocs, updateDoc, deleteDoc, query, where, serverTimestamp } from 'firebase/firestore';
import { COLLECTION_DOCTORS, SOURCE_SYSTEM, SCHEMA_VERSION } from '../shared/constants';

/**
 * Create a new doctor account
 * 
 * IMPORTANT: This creates a Firestore document only.
 * The Firebase Auth user is created by the Cloud Function trigger.
 * The Cloud Function will automatically send a password reset email.
 * 
 * NO PASSWORD GENERATION - Firebase handles this via email
 */
export async function createDoctor(doctorData) {
    try {
        const { email, ...profileData } = doctorData;

        // Format dates if provided
        const finalProfileData = { ...profileData };
        if (finalProfileData.membershipStartDate) {
            finalProfileData.membershipStartDate = new Date(finalProfileData.membershipStartDate);
        }
        if (finalProfileData.membershipEndDate) {
            finalProfileData.membershipEndDate = new Date(finalProfileData.membershipEndDate);
        }

        // Create doctor document in Firestore ONLY
        // The onDoctorCreated Cloud Function will create the Firebase Auth user
        // and handle the initial setup flow.
        const docRef = await addDoc(collection(db, COLLECTION_DOCTORS), {
            ...finalProfileData,
            email,
            profileCompleted: false,
            passwordChanged: false,
            sourceSystem: SOURCE_SYSTEM,
            schemaVersion: SCHEMA_VERSION,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
        });

        console.log(`✅ Doctor record created in Firestore: ${docRef.id}`);
        return { success: true, id: docRef.id };
    } catch (error) {
        console.error('Error creating doctor:', error);
        throw error;
    }
}

export async function getAllDoctors() {
    try {
        const querySnapshot = await getDocs(collection(db, COLLECTION_DOCTORS));
        return querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
        }));
    } catch (error) {
        console.error('Error getting doctors:', error);
        throw error;
    }
}

export async function getDoctorById(id) {
    if (!id) return null;
    try {
        const docRef = doc(db, COLLECTION_DOCTORS, id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            return { id: docSnap.id, ...docSnap.data() };
        }
        return null;
    } catch (error) {
        console.error('Error getting doctor:', error);
        throw error;
    }
}

export async function updateDoctor(id, updates) {
    try {
        const docRef = doc(db, COLLECTION_DOCTORS, id);
        await updateDoc(docRef, {
            ...updates,
            updatedAt: serverTimestamp(),
        });
        return { success: true };
    } catch (error) {
        console.error('Error updating doctor:', error);
        throw error;
    }
}

export async function deleteDoctor(id) {
    try {
        await deleteDoc(doc(db, COLLECTION_DOCTORS, id));
        return { success: true };
    } catch (error) {
        console.error('Error deleting doctor:', error);
        throw error;
    }
}

export async function getDoctorsByGuild(guildId) {
    try {
        const q = query(collection(db, COLLECTION_DOCTORS), where('guildId', '==', guildId));
        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
        }));
    } catch (error) {
        console.error('Error getting doctors by guild:', error);
        throw error;
    }
}

export async function getDoctorsByMultipleGuilds(guildIds) {
    if (!guildIds || guildIds.length === 0) return [];
    try {
        const q = query(collection(db, COLLECTION_DOCTORS), where('guildId', 'in', guildIds));
        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
        }));
    } catch (error) {
        console.error('Error getting doctors by multiple guilds:', error);
        throw error;
    }
}
